import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import ScrollToTop from "./components/ScrollToTop";
import { ContentProvider, useContentStatus } from "./data/content";
import Navbar from "./components/Navbar/Navbar";
import Footer from "./components/Footer/Footer";
import ContactSection from "./pages/PagesComps/ContactSection";
import Home from "./pages/Home";
import AllWork from "./pages/AllWork";
import ProjectDetail from "./pages/ProjectDetail";
import NotFound from "./pages/NotFound";

function AppLayout({ darkMode, toggleDarkMode, contactTick, setContactTick }) {
  const { pathname } = useLocation();
  const { status } = useContentStatus();
  const showFooter = pathname === "/" || pathname === "/work" || pathname.startsWith("/work/");

  // Content is fetched once at the root, so every page below renders with data
  // already in hand. Until it lands there is nothing to show but the background.
  if (status === "loading") {
    return <div className="min-h-screen bg-background-light dark:bg-background-dark" />;
  }

  if (status === "error") {
    return (
      <main className="min-h-screen bg-background-light dark:bg-background-dark flex items-center justify-center px-6">
        <p className="font-sans text-sm text-text-muted-light dark:text-text-muted-dark text-center max-w-xs leading-relaxed">
          Could not load the site content. Please refresh — if it keeps happening, the content service may be down.
        </p>
      </main>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        onSayHi={() => setContactTick((n) => n + 1)}
      />
      <Routes>
        <Route path="/"           element={<Home darkMode={darkMode} />} />
        <Route path="/work"       element={<AllWork />} />
        <Route path="/work/:slug" element={<ProjectDetail />} />
        <Route path="*"           element={<NotFound darkMode={darkMode} />} />
      </Routes>
      {showFooter && <ContactSection darkMode={darkMode} contactTick={contactTick} />}
      {showFooter && <Footer />}
    </>
  );
}

function App() {
  const [darkMode, setDarkMode] = useState(
    () => document.documentElement.classList.contains("dark")
  );
  const [contactTick, setContactTick] = useState(0);

  const setThemeColor = (dark) => {
    document.querySelectorAll('meta[name="theme-color"]').forEach((el) => {
      el.setAttribute("content", dark ? "#000000" : "#ffffff");
    });
  };

  const setFavicon = (dark) => {
    document.getElementById("favicon").setAttribute("href", dark ? "/favDark.svg" : "/favLight.svg");
  };

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => {
      localStorage.removeItem("theme");
      const next = e.matches;
      setDarkMode(next);
      document.documentElement.classList.toggle("dark", next);
      setThemeColor(next);
      setFavicon(next);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const toggleDarkMode = () => {
    setDarkMode((prev) => {
      const next = !prev;
      document.documentElement.classList.toggle("dark", next);
      localStorage.setItem("theme", next ? "dark" : "light");
      setThemeColor(next);
      setFavicon(next);
      return next;
    });
  };

  return (
    <BrowserRouter>
      <ContentProvider>
        <AppLayout
          darkMode={darkMode}
          toggleDarkMode={toggleDarkMode}
          contactTick={contactTick}
          setContactTick={setContactTick}
        />
      </ContentProvider>
      <Analytics />
      <SpeedInsights />
    </BrowserRouter>
  );
}

export default App;
